import { useEffect, useState } from 'react';

import { DesignerActions, useDesignerContext } from './useDesignerState';

type StepState = 'pending' | 'loading' | 'completed';

export function useLoaderState(gameId?: string | null) {
  const dispatch = useDesignerContext(context => context.dispatch);
  const loaderState = useDesignerContext(context =>
    gameId ? context.state.ui.loaderStates[gameId] : undefined,
  );

  // Use local state to trigger re-renders every 500ms
  const [, setTick] = useState(0);

  // Initialize state if gameId exists and state doesn't
  useEffect(() => {
    if (!gameId || loaderState) return;

    dispatch({
      type: DesignerActions.initLoaderState,
      payload: { gameId },
    });
  }, [gameId, loaderState, dispatch]);

  // Trigger re-renders every 500ms to update the calculated state
  useEffect(() => {
    if (!gameId || !loaderState) return;

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [gameId, loaderState]);

  // Return default state if not initialized yet
  if (!loaderState) {
    return {
      stepStates: ['pending', 'pending', 'pending', 'pending'] as StepState[],
      showPin: false,
    };
  }

  // Calculate current state based on elapsed time (pure calculation!)
  const elapsed = Date.now() - loaderState.startTime;

  let stepStates: StepState[];
  let showPin = false;

  const STEP_DURATION = 8000;

  if (elapsed < STEP_DURATION) {
    stepStates = ['loading', 'pending', 'pending', 'pending'];
    showPin = false;
  } else if (elapsed < 2 * STEP_DURATION) {
    stepStates = ['completed', 'loading', 'pending', 'pending'];
    showPin = true;
  } else if (elapsed < 3 * STEP_DURATION) {
    stepStates = ['completed', 'completed', 'loading', 'pending'];
    showPin = true;
  } else {
    stepStates = ['completed', 'completed', 'completed', 'loading'];
    showPin = true;
  }

  return { stepStates, showPin };
}
